'use client';

import { c as _c } from "react/compiler-runtime";
import type { QuickFilterPreset } from '@/components/filters/QuickFilters';
import { INSPIRATION_FILTER_KEYS } from '@/components/demand/InspirationFilters';
import { useQuickFilters, type UseQuickFiltersResult } from '@/lib/hooks/useQuickFilters';

/** Metadata key under which the Inspiration Feed quick-filter presets are stored. */
export const INSPIRATION_FEED_QUICK_FILTERS_METADATA_KEY = 'inspiration_quick_filters';

/**
 * Built-in default presets used when no `inspiration_quick_filters` metadata is
 * configured (or the stored value is invalid). Values must line up with the
 * shared demand-generation constants and only touch facets the feed filters on.
 */
export const DEFAULT_INSPIRATION_FEED_QUICK_FILTERS: readonly QuickFilterPreset[] = [{
  id: 'npd',
  label: 'New Product Development',
  filters: {
    // The development work before a product reaches market: shaping the idea,
    // validating the concept, formulating and testing it.
    productStages: ['idea', 'concept', 'develop', 'test']
  }
}, {
  id: 'product-launch',
  label: 'Product Launch',
  filters: {
    // Bringing the product to market and iterating after: launch readiness,
    // the launch itself, and post-launch refinement.
    productStages: ['pre-launch', 'launch', 'post-launch']
  }
}, {
  id: 'product-tasting-collabs',
  label: 'Product Tasting Collaborations',
  filters: {
    tactics: ['product-sampling', 'collaboration'],
    channels: ['retail-in-store']
  }
}, {
  id: 'in-store-sampling',
  label: 'In-store Sampling',
  filters: {
    tactics: ['product-sampling'],
    channels: ['retail-in-store'],
    activationFormats: ['sampling-station', 'in-store-demo']
  }
}, {
  id: 'social-campaigns',
  label: 'Social Campaigns',
  filters: {
    tactics: ['content-social-campaign'],
    channels: ['social']
  }
}, {
  id: 'retail-displays',
  label: 'Retail Displays & POS',
  filters: {
    tactics: ['retail-display-pos'],
    channels: ['retail-in-store']
  }
}, {
  id: 'promotions',
  label: 'Promotions & Offers',
  filters: {
    tactics: ['promotion-offer']
  }
}, {
  id: 'pop-ups',
  label: 'Pop-ups & Experiential',
  filters: {
    tactics: ['experiential-activation'],
    activationFormats: ['pop-up', 'experiential-installation']
  }
}, {
  id: 'referral-loyalty',
  label: 'Referral & Loyalty',
  filters: {
    tactics: ['referral-programme', 'loyalty-programme'],
    channels: ['email-crm']
  }
}];

/**
 * Retrieve the Inspiration Feed quick-filter presets. Thin wrapper around the
 * generic {@link useQuickFilters} that supplies the feed's metadata key,
 * defaults and facet whitelist. Pass `{ optimistic: false }` to hold off
 * rendering until the server response settles.
 */
export function useInspirationFeedQuickFilters(t0) {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let t2;
  if ($[2] !== options.optimistic) {
    t2 = {
      metadataKey: INSPIRATION_FEED_QUICK_FILTERS_METADATA_KEY,
      defaults: DEFAULT_INSPIRATION_FEED_QUICK_FILTERS,
      allowedFacetKeys: INSPIRATION_FILTER_KEYS,
      optimistic: options.optimistic
    };
    $[2] = options.optimistic;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useQuickFilters(t2);
}